/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: inherit
 */
import React, { useCallback, useEffect, useState } from 'react';
import { AppState, Linking, Platform, Pressable, StyleSheet, Text, View } from 'react-native';
import MapView, { Marker } from 'react-native-maps';
import { formatArPhoneDisplay, telHref } from '@shared/phone';
import { ticketColors, ticketFonts } from '../../../shared/theme/ticketColors';
import { spacing } from '../../../shared/theme/spacing';
import { supabase } from '../../../shared/supabase/supabaseClient';
import {
  fetchAssignmentForOrder,
  getDeliveryTracking,
  type DeliveryTrackingRow,
} from '../../infra/deliveryTrackingRepo';
import {
  DELIVERY_LEG_HINT_MERCHANT,
  DELIVERY_LEG_LABEL,
  resolveDeliveryLeg,
} from '../../domain/deliveryPhase';
import { useEmployeesStore, withSelfOwner } from '../../../team/store/useEmployeesStore';
import { canRenderNativeMapView } from '../../../shared/maps/canRenderNativeMapView';
import { captureCatch } from '../../../shared/sentry/sentryUser';

type Props = {
  orderId: string;
  branchId: string;
  isPlatformRider: boolean;
  deliveryLat?: number | null;
  deliveryLng?: number | null;
  deliveryAddress?: string | null;
  /** Código corto de bolsa (identidad del pedido). */
  displayCode?: string | null;
  /** Fired when delivery leg changes (e.g. delivered) so parent can reload order + rating. */
  onLegChange?: (leg: string | null) => void;
};

type AssignmentView = {
  id: string;
  status: string;
  rider_earning: number;
  rider_name: string | null;
  rider_phone: string | null;
};

/** ponytail: haversine / 20 km/h — same as portal live ETA */
function liveEtaMinutes(
  fromLat: number,
  fromLng: number,
  toLat: number,
  toLng: number,
): number {
  const R = 6371000;
  const dLat = ((toLat - fromLat) * Math.PI) / 180;
  const dLng = ((toLng - fromLng) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((fromLat * Math.PI) / 180) *
      Math.cos((toLat * Math.PI) / 180) *
      Math.sin(dLng / 2) ** 2;
  const meters = R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return Math.max(1, Math.ceil((meters / 20000) * 60));
}

function isTerminal(leg: string | null) {
  return leg === 'delivered' || leg === 'failed' || leg === 'cancelled';
}

function regionForPoints(
  points: { latitude: number; longitude: number }[],
): {
  latitude: number;
  longitude: number;
  latitudeDelta: number;
  longitudeDelta: number;
} {
  const lats = points.map((p) => p.latitude);
  const lngs = points.map((p) => p.longitude);
  const minLat = Math.min(...lats);
  const maxLat = Math.max(...lats);
  const minLng = Math.min(...lngs);
  const maxLng = Math.max(...lngs);
  return {
    latitude: (minLat + maxLat) / 2,
    longitude: (minLng + maxLng) / 2,
    latitudeDelta: Math.max((maxLat - minLat) * 1.5, 0.012),
    longitudeDelta: Math.max((maxLng - minLng) * 1.5, 0.012),
  };
}

/** Panel de tracking / rider para el comercio (ops en mostrador). */
export function MerchantDeliveryTrackingPanel({
  orderId,
  branchId,
  isPlatformRider,
  deliveryLat,
  deliveryLng,
  deliveryAddress,
  displayCode,
  onLegChange,
}: Props) {
  const employeesRaw = useEmployeesStore((s) => s.employees);
  const selfOwner = useEmployeesStore((s) => s.selfOwner);
  const employees = withSelfOwner(employeesRaw, selfOwner);
  const [tracking, setTracking] = useState<DeliveryTrackingRow | null>(null);
  const [assignment, setAssignment] = useState<AssignmentView | null>(null);
  const [storeCoords, setStoreCoords] = useState<{ lat: number; lng: number } | null>(null);

  const reload = useCallback(async () => {
    try {
      const [t, a] = await Promise.all([
        getDeliveryTracking(orderId),
        fetchAssignmentForOrder(orderId),
      ]);
      setTracking(t);
      setAssignment(
        a
          ? {
              id: a.id,
              status: a.status,
              rider_earning: a.rider_earning,
              rider_name: a.rider_name ?? null,
              rider_phone: a.rider_phone ?? null,
            }
          : null,
      );
    } catch (error) {
      // keep last good snapshot
      captureCatch('orders', 'delivery-tracking-reload')(error);
    }
  }, [orderId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const leg = resolveDeliveryLeg(assignment?.status ?? null, tracking?.status ?? null);
  const terminal = isTerminal(leg);

  useEffect(() => {
    onLegChange?.(leg);
  }, [leg, onLegChange]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const { data } = await supabase
        .from('branches')
        .select('latitude, longitude')
        .eq('id', branchId)
        .maybeSingle();
      if (cancelled || !data) return;
      const lat = data.latitude != null ? Number(data.latitude) : null;
      const lng = data.longitude != null ? Number(data.longitude) : null;
      if (lat != null && lng != null && Number.isFinite(lat) && Number.isFinite(lng)) {
        setStoreCoords({ lat, lng });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [branchId]);

  useEffect(() => {
    let channel: ReturnType<typeof supabase.channel> | null = null;
    let poll: ReturnType<typeof setInterval> | null = null;
    let cancelled = false;
    let visible = AppState.currentState === 'active';

    void (async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session?.access_token || cancelled) return;
      await supabase.realtime.setAuth(session.access_token);
      if (cancelled) return;

      channel = supabase
        .channel(`merchant-tracking-${orderId}`)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'delivery_tracking',
            filter: `order_id=eq.${orderId}`,
          },
          (payload) => {
            if (payload.new && typeof payload.new === 'object') {
              setTracking(payload.new as DeliveryTrackingRow);
            } else {
              void reload();
            }
          },
        )
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'delivery_assignments',
            filter: `order_id=eq.${orderId}`,
          },
          () => {
            void reload();
          },
        )
        .subscribe((status) => {
          if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
            void reload();
          }
        });
    })();

    // Live GPS only while en ruta; after delivered stop polling.
    if (!terminal) {
      poll = setInterval(() => {
        if (!visible || cancelled) return;
        void reload();
      }, 15_000);
    }

    const appSub = AppState.addEventListener('change', (state) => {
      visible = state === 'active';
      if (visible) void reload();
    });

    return () => {
      cancelled = true;
      if (poll) clearInterval(poll);
      if (channel) void supabase.removeChannel(channel);
      appSub.remove();
    };
  }, [orderId, reload, terminal]);

  if (!tracking && !assignment) return null;

  const title = leg ? DELIVERY_LEG_LABEL[leg] : 'Seguimiento';
  const hint = leg ? DELIVERY_LEG_HINT_MERCHANT[leg] : '';
  const lat = tracking?.lat ?? null;
  const lng = tracking?.lng ?? null;
  const hasRiderPos = typeof lat === 'number' && typeof lng === 'number';
  const destLat = deliveryLat ?? null;
  const destLng = deliveryLng ?? null;
  const storeLat = storeCoords?.lat ?? null;
  const storeLng = storeCoords?.lng ?? null;
  const showLiveMap = !terminal && (hasRiderPos || (typeof destLat === 'number' && typeof destLng === 'number'));
  const mapPoints: { latitude: number; longitude: number; title: string; pinColor?: string }[] = [];
  if (showLiveMap) {
    if (hasRiderPos) mapPoints.push({ latitude: lat!, longitude: lng!, title: 'Repartidor' });
    if (typeof storeLat === 'number' && typeof storeLng === 'number') {
      mapPoints.push({
        latitude: storeLat,
        longitude: storeLng,
        title: 'Local',
        pinColor: ticketColors.ink,
      });
    }
    if (typeof destLat === 'number' && typeof destLng === 'number') {
      mapPoints.push({
        latitude: destLat,
        longitude: destLng,
        title: 'Cliente',
        pinColor: ticketColors.gold,
      });
    }
  }
  const mapRegion = mapPoints.length > 0 ? regionForPoints(mapPoints) : null;
  const phoneHref = assignment?.rider_phone ? telHref(assignment.rider_phone) : null;
  // Rider contact while active; after delivered keep name only (rating lives outside).
  const showRider =
    isPlatformRider &&
    !!assignment?.rider_name &&
    leg !== 'offered' &&
    leg !== 'cancelled' &&
    leg != null;
  const showCall = showRider && !terminal && !!phoneHref;
  const updatedAt =
    !terminal && tracking?.last_updated_at
      ? new Date(tracking.last_updated_at).toLocaleTimeString('es-AR', {
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
        })
      : null;
  const etaMin =
    hasRiderPos &&
    !terminal &&
    typeof destLat === 'number' &&
    typeof destLng === 'number'
      ? liveEtaMinutes(lat!, lng!, destLat, destLng)
      : null;

  const code = displayCode?.trim() ? displayCode.trim().toUpperCase() : null;

  return (
    <View style={styles.card}>
      <Text style={styles.title}>Seguimiento</Text>
      {code ? (
        <View style={styles.codeBlock}>
          <Text style={styles.codeLabel}>Código del pedido</Text>
          <Text style={styles.displayCode}>{code}</Text>
          <Text style={styles.codeHint}>Identificá la bolsa con este código. No es el PIN del cliente.</Text>
        </View>
      ) : null}
      <Text style={styles.stageTitle}>{title}</Text>
      {hint ? <Text style={styles.hint}>{hint}</Text> : null}
      {etaMin != null ? <Text style={styles.eta}>Llega en ~{etaMin} min</Text> : null}

      {showRider ? (
        <View style={styles.riderBox}>
          <Text style={styles.riderName}>{assignment!.rider_name}</Text>
          {!terminal && assignment!.rider_earning > 0 ? (
            <Text style={styles.meta}>Gana ${Math.round(assignment!.rider_earning)}</Text>
          ) : null}
          {showCall ? (
            <Pressable
              onPress={() => {
                if (phoneHref) void Linking.openURL(phoneHref);
              }}
              style={styles.callBtn}
              accessibilityRole="button"
            >
              <Text style={styles.callBtnText}>
                Llamar · {formatArPhoneDisplay(assignment!.rider_phone)}
              </Text>
            </Pressable>
          ) : null}
        </View>
      ) : null}

      {!isPlatformRider && !terminal && tracking?.assigned_to ? (
        <Text style={styles.meta}>
          Empleado:{' '}
          {employees.find((e) => e.authUserId === tracking.assigned_to)?.name ??
            `${tracking.assigned_to.slice(0, 8)}…`}
        </Text>
      ) : null}

      {!terminal && deliveryAddress ? (
        <Text style={styles.meta} numberOfLines={2}>
          Destino: {deliveryAddress}
        </Text>
      ) : null}

      {updatedAt ? <Text style={styles.meta}>Última vez · {updatedAt}</Text> : null}

      {mapRegion && Platform.OS !== 'web' && canRenderNativeMapView() ? (
        <MapView style={styles.map} region={mapRegion}>
          {mapPoints.map((p) => (
            <Marker
              key={p.title}
              coordinate={{ latitude: p.latitude, longitude: p.longitude }}
              title={p.title}
              pinColor={p.pinColor}
            />
          ))}
        </MapView>
      ) : null}

      {mapRegion && Platform.OS === 'web' ? (
        <Pressable
          onPress={() =>
            void Linking.openURL(
              `https://www.google.com/maps/dir/?api=1&destination=${
                typeof destLat === 'number' && typeof destLng === 'number'
                  ? `${destLat},${destLng}`
                  : `${mapRegion.latitude},${mapRegion.longitude}`
              }`,
            )
          }
        >
          <Text style={styles.link}>Ver ruta en Maps</Text>
        </Pressable>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  card: {
    gap: 6,
    padding: spacing.md,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper,
    marginTop: spacing.sm,
  },
  title: {
    fontSize: 12,
    fontWeight: '700',
    color: ticketColors.ink2,
    fontFamily: ticketFonts.sans,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  },
  codeBlock: {
    alignItems: 'center',
    gap: 4,
    marginBottom: 4,
  },
  codeLabel: {
    fontSize: 11,
    fontWeight: '700',
    letterSpacing: 0.4,
    textTransform: 'uppercase',
    color: ticketColors.ink3,
    fontFamily: ticketFonts.sans,
  },
  codeHint: {
    fontSize: 12,
    fontWeight: '600',
    color: ticketColors.ink3,
    textAlign: 'center',
    fontFamily: ticketFonts.sans,
    paddingHorizontal: spacing.sm,
  },
  displayCode: {
    fontSize: 28,
    fontWeight: '800',
    letterSpacing: 4,
    color: ticketColors.ink,
    fontFamily: ticketFonts.sans,
  },
  stageTitle: {
    fontSize: 16,
    fontWeight: '800',
    color: ticketColors.ink,
    fontFamily: ticketFonts.sans,
  },
  hint: {
    fontSize: 13,
    color: ticketColors.ink2,
    fontFamily: ticketFonts.sans,
  },
  eta: {
    fontSize: 14,
    fontWeight: '800',
    color: ticketColors.ink,
    fontFamily: ticketFonts.sans,
  },
  riderBox: {
    marginTop: 4,
    gap: 4,
    padding: spacing.sm,
    borderRadius: 10,
    backgroundColor: ticketColors.paper2,
  },
  riderName: {
    fontSize: 15,
    fontWeight: '800',
    color: ticketColors.ink,
    fontFamily: ticketFonts.sans,
  },
  meta: {
    fontSize: 13,
    color: ticketColors.ink2,
    fontFamily: ticketFonts.sans,
  },
  callBtn: {
    alignSelf: 'flex-start',
    marginTop: 4,
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 8,
    backgroundColor: ticketColors.ink,
  },
  callBtnText: {
    fontSize: 13,
    fontWeight: '700',
    color: ticketColors.paper,
    fontFamily: ticketFonts.sans,
  },
  link: {
    fontSize: 13,
    fontWeight: '700',
    color: ticketColors.gold,
    fontFamily: ticketFonts.sans,
    marginTop: 4,
  },
  map: {
    height: 180,
    borderRadius: 10,
    marginTop: 6,
  },
});
